import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Alert, Platform, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { callMiDeudaDeLogistica, callPagarComisionAhora } from '../services/cloudFunctions';
import { VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';
import { desgloseDeComision } from '../constants/comisionLogistica';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOGÍSTICA — la puerta del renglón de vehículos de mayor categoría
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «que tenga sus funciones, que diga logística o como tú le vayas a poner, que
 * el usuario sepa que ahí es donde están las gestiones para vehículo de mayor
 * categoría» (fundador, 5-oct-2026).
 *
 * Esta pantalla tiene CUATRO caras, según en qué punto esté la persona, y las
 * cuatro tienen que decir claramente qué pasa y qué sigue. La razón de ponerle
 * tanto cuidado a esto: el fundador ya se topó en este proyecto con pantallas
 * donde «el usuario queda atrapado, no lo deja salir aunque use la flecha de
 * salida». Un estado sin salida y sin explicación es el fallo que más veces se
 * ha repetido aquí.
 *
 *   · SIN ALTA      → qué es esto y el botón para registrar el vehículo.
 *   · PENDIENTE     → «lo estamos revisando», con lo que mandó a la vista.
 *   · RECHAZADO     → el motivo EN CLARO y el botón para corregirlo.
 *   · APROBADO      → lo que debe de comisión y sus trabajos.
 *
 * Y siempre, en las cuatro, la flecha de volver funciona.
 */
export default function LogisticaScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [cargando, setCargando] = React.useState(true);
  const [proveedor, setProveedor] = React.useState<any | null>(null);
  /**
   * El DESGLOSE de la deuda. El número solo no basta: «RD$540» sin decir de
   * qué viene es una factura sin concepto, y el fundador puso la transparencia
   * como requisito del renglón. El documento del proveedor no lo trae — los
   * asientos viven en una colección cerrada a propósito— así que viene por
   * callable, que lee con Admin SDK.
   */
  const [desglose, setDesglose] = React.useState<any | null>(null);
  const [ocupado, setOcupado] = React.useState<string | null>(null);

  const avisar = (titulo: string, texto: string) => {
    // `Alert.alert` es un no-op en react-native-web y esta pantalla corre ahi.
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  /** Abre una pagina de Stripe. En web se navega; en el telefono, navegador. */
  const abrir = async (url: string) => {
    try {
      if (Platform.OS === 'web') window.location.href = url;
      else await Linking.openURL(url);
    } catch {
      avisar('No pudimos abrir la pagina de pago', 'Copia este enlace en tu navegador:\n\n' + url);
    }
  };

  React.useEffect(() => {
    if (!uid) {
      setCargando(false);
      return;
    }
    let vivo = true;
    const parar = firestoreService.listenToProveedorLogistica(uid, (datos) => {
      if (!vivo) return;
      setProveedor(datos);
      setCargando(false);
    });
    // ══ UNA RULETA ETERNA ES PEOR QUE UN ERROR ═══════════════════════
    //
    // Un listener de Firestore sin conexión NO llama al callback ni con error:
    // simplemente no dice nada. Sin este corte, alguien con mala señal abre
    // esta pantalla y se queda mirando un círculo girando sin explicación — y
    // «la app no carga» es literalmente uno de los rechazos que ya nos
    // costaron una versión.
    //
    // A los 8 segundos se deja de esperar y se pinta el estado SIN ALTA, que
    // es la suposición segura: casi nadie tiene un camión, y desde ahí las dos
    // puertas funcionan. Si el dato llega después, el listener lo pinta.
    const corte = setTimeout(() => {
      if (vivo) setCargando(false);
    }, 8000);
    return () => {
      vivo = false;
      clearTimeout(corte);
      // El listener puede ser la función de corte de native o la de web.
      try {
        if (typeof parar === 'function') parar();
      } catch {
        /* desmontaje: un fallo al cortar no le importa a nadie */
      }
    };
  }, [uid]);

  // Se pide una sola vez por apertura, y solo si hay alta aprobada: es una
  // llamada al servidor, no un listener. La deuda viva se sigue leyendo del
  // documento del proveedor, que sí es en vivo.
  React.useEffect(() => {
    if (proveedor?.estado !== 'APROBADO') return;
    let vivo = true;
    callMiDeudaDeLogistica({})
      .then((r) => {
        if (vivo) setDesglose(r?.data ?? null);
      })
      .catch((e: any) => {
        // Sin desglose la pantalla sigue sirviendo: se ve el total. Un fallo
        // aquí no puede dejarla en blanco.
        console.warn('[logistica] no se pudo traer el desglose:', e?.message);
      });
    return () => {
      vivo = false;
    };
  }, [proveedor?.estado]);

  const pagarAhora = async () => {
    setOcupado('pagar');
    try {
      const r = await callPagarComisionAhora({});
      const url = String(r?.data?.url ?? '');
      if (!url) throw new Error('sin enlace');
      await abrir(url);
    } catch (e: any) {
      // El servidor dice por qué — p. ej. que es menos del mínimo que Stripe
      // acepta y se junta con el próximo servicio. Eso se enseña tal cual.
      avisar('No se puede cobrar ahora', String(e?.message ?? 'Inténtalo más tarde.'));
    } finally {
      setOcupado(null);
    }
  };

  // El candado de RUTA. Esconder la tarjeta no basta: la URL de expo-router
  // sigue existiendo. Mientras el circuito no cierre de punta a punta, esto no
  // se ve — es la lección medida del vertical escolar.
  if (!VERTICAL_LOGISTICA_VISIBLE) return null;

  const estado: string | null = proveedor?.estado ?? null;
  const debe = Number(proveedor?.comisionPendiente ?? 0) || 0;

  return (
    <ScreenBackground>
      <PantallaSegura>
        <View style={[estilos.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={estilos.salida}>
            <Ionicons name="chevron-back" size={22} color={colors.text} />
            <Text style={[estilos.salidaTexto, { color: colors.text }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={estilos.cuerpo}>
          <Text style={[estilos.titulo, { color: colors.text }]}>Logística y Carga</Text>

          {cargando && (
            <View style={estilos.centro}>
              <ActivityIndicator color={colors.primary} />
            </View>
          )}

          {!cargando && !estado && (
            <>
              <Text style={[estilos.bajada, { color: colors.textSecondary }]}>
                Aquí se gestionan los vehículos de mayor categoría: camioneta, camión, van y
                autobús. Si tienes uno, puedes recibir trabajos de curier, mudanza, carga y
                giras.
              </Text>

              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[estilos.bloqueTitulo, { color: colors.text }]}>Cómo funciona</Text>
                {[
                  'Registras tu vehículo y tus papeles. Nosotros los revisamos.',
                  'Te llegan los trabajos que encajan con lo que tienes.',
                  'Tú pones el precio. El cliente lo acepta o no.',
                  'Al entregar, el cliente te da un PIN. Sin ese PIN no se cierra el trabajo.',
                  'El cliente te paga a ti, directo. Nosotros no retenemos tu dinero.',
                ].map((paso, i) => (
                  <View key={i} style={estilos.paso}>
                    <Text style={[estilos.pasoNumero, { color: colors.primary }]}>{i + 1}</Text>
                    <Text style={[estilos.pasoTexto, { color: colors.textSecondary }]}>{paso}</Text>
                  </View>
                ))}
              </View>

              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[estilos.bloqueTitulo, { color: colors.text }]}>Lo que cuesta</Text>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  No hay mensualidad en este renglón. Se cobra una comisión solo por el trabajo
                  que hagas, y el porcentaje baja cuanto más grande es el trabajo.
                </Text>
                {[2000, 10000, 20000].map((monto) => {
                  const d = desgloseDeComision(monto);
                  return (
                    <Text key={monto} style={[estilos.ejemplo, { color: colors.text }]}>
                      {`RD$${d.monto.toLocaleString('es-DO')} → comisión RD$${d.comision.toLocaleString('es-DO')} · tú recibes RD$${d.recibe.toLocaleString('es-DO')}`}
                    </Text>
                  );
                })}
              </View>

              {/* ══ DOS CAMINOS, Y ANTES SOLO HABÍA UNO ═════════════════════
                  Esta pantalla daba por hecho que quien entra quiere SER
                  transportista, y le ofrecía solo registrar su vehículo. Pero a
                  la tarjeta de «Transporte de Carga» del perfil también llega
                  quien lo que quiere es MOVER algo — que de hecho va a ser la
                  mayoría. Dejarlo sin salida era mandarlo a registrar un
                  camión que no tiene. */}
              <TouchableOpacity
                onPress={() => router.push('/logistica-pedir' as any)}
                style={[estilos.boton, { backgroundColor: '#EF6C00' }]}
              >
                <Text style={estilos.botonTexto}>Necesito mover algo</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => router.push('/logistica-alta' as any)}
                style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 10 }]}
              >
                <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>
                  Tengo un vehículo y quiero trabajar
                </Text>
              </TouchableOpacity>
            </>
          )}

          {!cargando && estado === 'PENDIENTE' && (
            <>
              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: '#D97706' }]}>
                <View style={estilos.filaIcono}>
                  <Ionicons name="time-outline" size={22} color="#D97706" />
                  <Text style={[estilos.bloqueTitulo, { color: colors.text, marginBottom: 0 }]}>
                    Estamos revisando tus papeles
                  </Text>
                </View>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  Una persona de Go Safely está mirando la foto de tu {String(proveedor?.tipoVehiculo ?? 'vehículo').toLowerCase()},
                  tu matrícula, tu licencia y tu seguro. Te avisamos en cuanto esté listo.
                </Text>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  Mientras tanto puedes seguir usando la app como siempre: esto no te cambió de
                  perfil ni te quitó nada.
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => router.push('/logistica-alta' as any)}
                style={[estilos.botonSuave, { borderColor: colors.border }]}
              >
                <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>Corregir lo que mandé</Text>
              </TouchableOpacity>
            </>
          )}

          {!cargando && estado === 'RECHAZADO' && (
            <>
              {/* El motivo EN CLARO. Un rechazo sin motivo deja a la persona sin
                  saber qué arreglar, y es la clase de callejón sin salida que el
                  fundador ya señaló en otra pantalla de este proyecto. */}
              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: '#DC2626' }]}>
                <View style={estilos.filaIcono}>
                  <Ionicons name="alert-circle-outline" size={22} color="#DC2626" />
                  <Text style={[estilos.bloqueTitulo, { color: colors.text, marginBottom: 0 }]}>
                    No pudimos aprobarlo
                  </Text>
                </View>
                <Text style={[estilos.bloqueTexto, { color: colors.text }]}>
                  {String(proveedor?.motivoRechazo ?? '').trim() ||
                    'No se indicó un motivo. Escríbenos y lo revisamos contigo.'}
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => router.push('/logistica-alta' as any)}
                style={[estilos.boton, { backgroundColor: colors.primary }]}
              >
                <Text style={estilos.botonTexto}>Corregir y volver a enviar</Text>
              </TouchableOpacity>
            </>
          )}

          {!cargando && estado === 'APROBADO' && (
            <>
              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: '#10B981' }]}>
                <View style={estilos.filaIcono}>
                  <Ionicons name="checkmark-circle" size={22} color="#10B981" />
                  <Text style={[estilos.bloqueTitulo, { color: colors.text, marginBottom: 0 }]}>
                    Tu {String(proveedor?.tipoVehiculo ?? '').toLowerCase()} está aprobada
                  </Text>
                </View>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  Placa {String(proveedor?.placa ?? '—')} · {String(proveedor?.capacidad ?? '')}
                </Text>
              </View>

              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[estilos.bloqueTitulo, { color: colors.text }]}>Tu comisión</Text>
                <Text style={[estilos.saldo, { color: debe > 0 ? '#D97706' : '#10B981' }]}>
                  {`RD$${debe.toLocaleString('es-DO')}`}
                </Text>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  {debe > 0
                    ? 'Es lo que llevas acumulado de los trabajos que has hecho. Si dejas saldo en tu billetera, se descuenta solo al terminar cada uno.'
                    : 'No debes nada. Si dejas saldo en tu billetera, la comisión se descuenta sola al terminar cada trabajo.'}
                </Text>

                {/* ══ SU TOPE ══════════════════════════════════════════════════
                    Un límite que no se enseña es una sorpresa: el día que se le
                    frene no va a entender por qué y va a pensar que la app se
                    rompió. Y enseñarlo es también enseñar el premio: sube con
                    los trabajos entregados. */}
                {typeof desglose?.topeDeSaldo === 'number' && desglose.topeDeSaldo > 0 && (
                  <Text style={[estilos.bloqueTexto, { color: colors.textSecondary, marginTop: 8 }]}>
                    {`Puedes deber hasta RD$${desglose.topeDeSaldo.toLocaleString('es-DO')}. ` +
                      (debe >= desglose.topeDeSaldo
                        ? 'Ya llegaste: paga para volver a aceptar trabajos nuevos. Lo que tengas en curso lo terminas igual.'
                        : 'Pasado eso, no puedes aceptar trabajos nuevos hasta pagar — pero el que tengas en curso lo terminas igual. El tope sube a medida que entregas.')}
                  </Text>
                )}

                {/* DE QUÉ VIENE. Un total sin concepto es una factura sin
                    concepto — y en este renglón la transparencia es requisito,
                    no cortesía. */}
                {Array.isArray(desglose?.servicios) && desglose.servicios.length > 0 && (
                  <View style={{ marginTop: 10 }}>
                    {desglose.servicios.map((x: any) => (
                      <View key={String(x.servicioId)} style={estilos.filaDesglose}>
                        <Text style={[estilos.desgloseTexto, { color: colors.textSecondary }]}>
                          {`Trabajo de ${Number(x.monto ?? 0).toLocaleString('es-DO')}`}
                        </Text>
                        <Text style={[estilos.desgloseMonto, { color: colors.text }]}>
                          {`RD$${Number(x.comision ?? 0).toLocaleString('es-DO')}`}
                        </Text>
                      </View>
                    ))}
                    {typeof desglose?.cobroAgrupadoDesde === 'number' && debe > 0 && (
                      <Text style={[estilos.bloqueTexto, { color: colors.textSecondary, marginTop: 8 }]}>
                        {debe >= desglose.cobroAgrupadoDesde
                          ? 'Ya pasaste el monto para el cobro: se te cobra en la próxima pasada.'
                          : `Se cobra al llegar a RD$${desglose.cobroAgrupadoDesde.toLocaleString('es-DO')}, o el lunes.`}
                      </Text>
                    )}
                  </View>
                )}
              </View>

              <TouchableOpacity
                onPress={() => router.push('/logistica-trabajos' as any)}
                style={[estilos.boton, { backgroundColor: '#EF6C00' }]}
              >
                <Text style={estilos.botonTexto}>Buscar trabajos</Text>
              </TouchableOpacity>

              {/* ══ CÓMO SE PAGA ════════════════════════════════════════════
                  **No guardamos tarjetas.** Es decisión del fundador y además es
                  lo que dicen los Términos y la Política de Privacidad, así que
                  aquí no hay nada que «vincular».

                  Lo que hay son dos caminos que sí funcionan en el país: la
                  BILLETERA —que se recarga por depósito o transferencia con
                  foto del comprobante— y un enlace de pago de un solo uso.

                  Y el orden importa: con saldo en la billetera, la comisión se
                  descuenta sola **al terminar el trabajo**, que es cuando acaba
                  de cobrar en mano. Sin saldo, se le acumula y paga cuando
                  pueda. */}
              <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[estilos.bloqueTitulo, { color: colors.text }]}>Cómo se paga</Text>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  Si tienes saldo en tu billetera, la comisión se descuenta sola al terminar
                  cada trabajo — justo cuando el cliente te acaba de pagar. Si no tienes, se te
                  acumula y la pagas cuando puedas.
                </Text>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  No te pedimos guardar ninguna tarjeta. Recargas por depósito o transferencia,
                  o pagas aquí con tarjeta una sola vez.
                </Text>

                <TouchableOpacity
                  onPress={() => router.push('/wallet' as any)}
                  style={[estilos.botonSuave, { borderColor: colors.border }]}
                >
                  <Ionicons name="wallet-outline" size={16} color={colors.text} />
                  <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>
                    Recargar mi billetera
                  </Text>
                </TouchableOpacity>

                {debe > 0 && (
                  <TouchableOpacity
                    onPress={pagarAhora}
                    disabled={ocupado === 'pagar'}
                    style={[estilos.boton, { backgroundColor: '#EF6C00' }]}
                  >
                    {ocupado === 'pagar' ? (
                      <ActivityIndicator color="#FFF" />
                    ) : (
                      <Text style={estilos.botonTexto}>
                        {`Pagar ahora RD$${debe.toLocaleString('es-DO')}`}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {proveedor?.bloqueadoPorDeuda === true && (
                <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: '#DC2626' }]}>
                  <View style={estilos.filaIcono}>
                    <Ionicons name="pause-circle-outline" size={22} color="#DC2626" />
                    <Text style={[estilos.bloqueTitulo, { color: colors.text, marginBottom: 0 }]}>
                      No puedes aceptar trabajos nuevos
                    </Text>
                  </View>
                  <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                    Tienes comisión pendiente. Los trabajos que ya tengas en curso los puedes
                    terminar con normalidad — eso no se toca.
                  </Text>
                </View>
              )}
            </>
          )}

          {!cargando && (
            <TouchableOpacity
              onPress={() => router.push('/logistica-mis-pedidos' as any)}
              style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 4 }]}
            >
              <Ionicons name="list-outline" size={16} color={colors.text} />
              <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>
                Los transportes que yo pedí
              </Text>
            </TouchableOpacity>
          )}

          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  cabecera: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  salida: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4 },
  salidaTexto: { fontSize: 16, marginLeft: 2 },
  cuerpo: { paddingHorizontal: 16, paddingTop: 8 },
  centro: { paddingVertical: 40, alignItems: 'center' },
  titulo: { fontSize: 26, fontWeight: '700', marginBottom: 8 },
  bajada: { fontSize: 14, lineHeight: 21, marginBottom: 16 },
  bloque: { borderRadius: 14, borderWidth: 1, padding: 16, marginBottom: 14 },
  bloqueTitulo: { fontSize: 16, fontWeight: '700', marginBottom: 10, flex: 1 },
  bloqueTexto: { fontSize: 14, lineHeight: 21, marginBottom: 6 },
  filaIcono: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  paso: { flexDirection: 'row', gap: 12, marginBottom: 10 },
  pasoNumero: { fontSize: 15, fontWeight: '800', width: 16 },
  pasoTexto: { fontSize: 14, lineHeight: 20, flex: 1 },
  ejemplo: { fontSize: 13, lineHeight: 21, fontWeight: '600', marginTop: 4 },
  saldo: { fontSize: 30, fontWeight: '800', marginBottom: 8 },
  filaDesglose: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  desgloseTexto: { fontSize: 13 },
  desgloseMonto: { fontSize: 13, fontWeight: '700' },
  boton: { borderRadius: 14, paddingVertical: 16, alignItems: 'center', marginTop: 6 },
  botonTexto: { color: '#FFF', fontSize: 16, fontWeight: '700' },
  botonSuave: { flexDirection: 'row', gap: 8, borderRadius: 14, paddingVertical: 14, alignItems: 'center', justifyContent: 'center', borderWidth: 1, marginTop: 2 },
  botonSuaveTexto: { fontSize: 15, fontWeight: '600' },
});
